/**
 * Google Analytics Helper Functions
 *
 * Provides type-safe wrapper functions for common GA events.
 * Use these throughout your app for consistent event tracking.
 */

/**
 * Check if gtag is available
 */
function isGtagAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.gtag === "function"
}

/**
 * Check if the current user is a streamer based on stored user info.
 * All analytics events are restricted to streamer accounts only.
 */
function isStreamer(): boolean {
  if (typeof window === "undefined") return false
  try {
    const userInfoStr = localStorage.getItem("user_info")
    if (!userInfoStr) return false
    const userInfo = JSON.parse(userInfoStr)
    return userInfo.data?.accountType === "STREAMER"
  } catch {
    return false
  }
}

/**
 * Track a page view (streamer only)
 * Useful for SPAs where navigation doesn't trigger automatic page views
 */
export function trackPageView(url: string, title?: string) {
  if (!isGtagAvailable() || !isStreamer()) return

  window.gtag("event", "page_view", {
    page_path: url,
    page_title: title,
  })
}

/**
 * Track a custom event (streamer only)
 */
export function trackEvent(
  eventName: string,
  params?: {
    category?: string
    label?: string
    value?: number
    [key: string]: unknown
  }
) {
  if (!isGtagAvailable() || !isStreamer()) {
    return
  }

  window.gtag("event", eventName, {
    event_category: params?.category,
    event_label: params?.label,
    value: params?.value,
    ...params,
  })
}

/**
 * Track button/link clicks
 */
export function trackClick(elementName: string, category = "engagement") {
  trackEvent("click", {
    category,
    label: elementName,
  })
}

/**
 * Track form submissions
 */
export function trackFormSubmit(formName: string, success = true) {
  trackEvent("form_submit", {
    category: "forms",
    label: formName,
    value: success ? 1 : 0,
    success,
  })
}

/**
 * Track user login
 */
export function trackLogin(method: string) {
  trackEvent("login", {
    method,
  })
}

/**
 * Track user logout
 */
export function trackLogout() {
  trackEvent("logout", {
    category: "user",
  })
}

/**
 * Track search queries
 */
export function trackSearch(searchTerm: string, resultCount?: number) {
  trackEvent("search", {
    search_term: searchTerm,
    result_count: resultCount,
  })
}

/**
 * Track video plays (for game demos, etc.)
 */
export function trackVideoPlay(videoName: string, videoUrl?: string) {
  trackEvent("video_start", {
    category: "video",
    label: videoName,
    video_url: videoUrl,
  })
}

/**
 * Track file downloads
 */
export function trackDownload(fileName: string, fileType?: string) {
  trackEvent("file_download", {
    category: "downloads",
    label: fileName,
    file_type: fileType,
  })
}

/**
 * Track errors
 */
export function trackError(
  errorMessage: string,
  errorType = "javascript_error"
) {
  trackEvent("exception", {
    description: errorMessage,
    fatal: false,
    error_type: errorType,
  })
}

/**
 * Track timing/performance
 */
export function trackTiming(
  name: string,
  value: number,
  category = "performance"
) {
  trackEvent("timing_complete", {
    name,
    value,
    event_category: category,
  })
}

/**
 * Set user properties (streamer only)
 * Useful for tracking user types (admin, streamer, etc.)
 */
export function setUserProperties(properties: {
  user_id?: string
  user_type?: string
  [key: string]: unknown
}) {
  if (!isGtagAvailable() || !isStreamer()) return

  window.gtag("set", "user_properties", properties)
}

/**
 * Track custom game-related events
 */
export const gameAnalytics = {
  /**
   * Track when a user views a game
   */
  viewGame(
    gameName: string,
    gameType: "SLOTS" | "LIVE_CASINO" | "CRASH_GAMES"
  ) {
    trackEvent("view_game", {
      category: "games",
      label: gameName,
      game_type: gameType,
    })
  },

  /**
   * Track when a user plays a demo
   */
  playDemo(gameName: string, gameSymbol?: string) {
    trackEvent("play_demo", {
      category: "games",
      label: gameName,
      game_symbol: gameSymbol,
    })
  },

  /**
   * Track roadmap views
   */
  viewRoadmap() {
    trackEvent("view_roadmap", {
      category: "roadmap",
    })
  },

  /**
   * Track library views
   */
  viewLibrary(gameType: "SLOTS" | "LIVE_CASINO") {
    trackEvent("view_library", {
      category: "library",
      game_type: gameType,
    })
  },

  /**
   * Track when a user views a media pack / assets modal
   */
  viewMediaPack(gameName: string) {
    trackEvent("view_media_pack", {
      category: "games",
      label: gameName,
    })
  },
}

/**
 * Track streamer-specific events
 * Note: The isStreamer() guard in trackEvent already ensures these only fire for streamers.
 */
export const streamerAnalytics = {
  /**
   * Track when a streamer successfully completes first-time password change
   */
  firstPasswordChangeSuccess() {
    trackEvent("streamer_first_password_change", {
      category: "streamer_auth",
      label: "success",
    })
  },

  /**
   * Track when a streamer successfully logs in
   */
  loginSuccess(method: "email" | "sso") {
    trackEvent("streamer_login", {
      category: "streamer_auth",
      label: method,
    })
  },

  /**
   * Track when a streamer accepts the privacy policy
   */
  privacyPolicyAccepted() {
    trackEvent("streamer_privacy_policy_accepted", {
      category: "streamer_auth",
      label: "accepted",
    })
  },

  /**
   * Track when a streamer declines the privacy policy
   */
  privacyPolicyDeclined() {
    trackEvent("streamer_privacy_policy_declined", {
      category: "streamer_auth",
      label: "declined",
    })
  },

  /**
   * Track when a streamer completes forgot password reset
   */
  forgotPasswordResetSuccess() {
    trackEvent("streamer_forgot_password_reset", {
      category: "streamer_auth",
      label: "success",
    })
  },
}

/**
 * Track admin-specific actions
 */
export const adminAnalytics = {
  /**
   * Track streamer management actions
   */
  manageStreamer(action: "add" | "edit" | "delete", streamerId?: string) {
    trackEvent("manage_streamer", {
      category: "admin",
      label: action,
      streamer_id: streamerId,
    })
  },

  /**
   * Track admin management actions
   */
  manageAdmin(action: "add" | "edit" | "delete", adminId?: string) {
    trackEvent("manage_admin", {
      category: "admin",
      label: action,
      admin_id: adminId,
    })
  },

  /**
   * Track report views
   */
  viewReports() {
    trackEvent("view_reports", {
      category: "admin",
    })
  },
}
